/**
 * Staff masquerade: swap the browser Supabase session to another user,
 * then restore the original session on exit.
 *
 * The staff refresh token is kept in sessionStorage so Exit survives a reload
 * of this tab. It does not carry to other tabs. This is the staff member's
 * own browser only.
 */
import { supabase } from './supabase';
import { getAuthHeaders } from './api';

const ORIGIN_KEY = 'nf_masq_origin';
const ACTIVE_KEY = 'nf_masq_active';
const STAFF_DOMAIN = 'leadgenpros.io';

export function isMasqueradeAdminEmail(email) {
  return typeof email === 'string' && email.trim().toLowerCase().endsWith(`@${STAFF_DOMAIN}`);
}

export function readMasqueradeSession() {
  try {
    const raw = sessionStorage.getItem(ACTIVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.targetUserId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export async function startMasquerade(targetUserId) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token || !session?.refresh_token) {
    throw new Error('Your session has expired. Please sign in again.');
  }

  sessionStorage.setItem(ORIGIN_KEY, JSON.stringify({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    email: session.user?.email || '',
  }));

  let switched = false;
  try {
    const headers = await getAuthHeaders();
    const res = await fetch('/api/admin/masquerade-start', {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetUserId }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(payload.error || 'Could not start masquerade');
    }
    if (!payload.tokenHash || !payload.otpType) {
      throw new Error('Login link was incomplete');
    }

    const { error } = await supabase.auth.verifyOtp({
      token_hash: payload.tokenHash,
      type: payload.otpType,
    });
    if (error) {
      throw new Error(error.message || 'Could not switch session');
    }

    switched = true;
    sessionStorage.setItem(ACTIVE_KEY, JSON.stringify({
      targetName: payload.target?.fullName || payload.target?.email || 'user',
      targetEmail: payload.target?.email || '',
      targetUserId: payload.target?.id || targetUserId,
    }));
    window.location.assign('/client-management');
  } catch (err) {
    if (!switched) {
      sessionStorage.removeItem(ORIGIN_KEY);
      sessionStorage.removeItem(ACTIVE_KEY);
    }
    throw err;
  }
}

export async function stopMasquerade() {
  const raw = sessionStorage.getItem(ORIGIN_KEY);
  if (!raw) {
    throw new Error('No masquerade session to exit. Sign out and sign back in.');
  }

  let origin;
  let active = {};
  try {
    origin = JSON.parse(raw);
    const activeRaw = sessionStorage.getItem(ACTIVE_KEY);
    active = activeRaw ? JSON.parse(activeRaw) : {};
  } catch {
    throw new Error('Masquerade session is unreadable. Sign out and sign back in.');
  }

  const { error } = await supabase.auth.setSession({
    access_token: origin.access_token,
    refresh_token: origin.refresh_token,
  });
  if (error) {
    throw new Error(error.message || 'Could not restore your session. Sign out and sign back in.');
  }

  if (active.targetUserId) {
    try {
      const headers = await getAuthHeaders();
      await fetch('/api/admin/masquerade-stop', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserId: active.targetUserId }),
      });
    } catch (auditError) {
      console.error('masquerade stop audit failed', auditError);
    }
  }

  sessionStorage.removeItem(ORIGIN_KEY);
  sessionStorage.removeItem(ACTIVE_KEY);
  window.location.assign('/admin/masquerade');
}
